import client, {QueryOptions, MutationOptions} from "@irati/utils/services/apollo"
import {Tenant, User} from "@irati/types"
import {
  QueryPasswordRequestForgotArgs,
  QueryResendWelcomeEmailsArgs,
  MutationPasswordSetArgs,
  MutationPasswordSetFromForgotArgs,
  MutationPasswordSetFromLoginChallengeArgs,
  PasswordChallenge
} from "@irati/types/ops"
import PBService from "@irati/utils/services"

import loginErrorHandler from "./errors/legacy/loginErrorHandler"

import passwordRequestForgotQuery, {passwordRequestForgotKey} from "./queries/passwordRequestForgot"
import passwordSetMutation, {passwordSetKey} from "./mutations/passwordSet"
import passwordSetFromForgotMutation, {passwordSetFromForgotKey} from "./mutations/passwordSetFromForgot"
import passwordSetFromLoginChallengeMutation, {passwordSetFromLoginChallengeKey} from "./mutations/passwordSetFromLoginChallenge"
import resendWelcomeEmailsQuery, {resendWelcomeEmailsKey} from "./queries/resendWelcomeEmails"

class Password extends PBService {
  forgot(email: string, tenant: Tenant, options: Omit<QueryOptions, "fetchPolicy"> = {}) {
    const variables: QueryPasswordRequestForgotArgs = {email, businessId: tenant.uuid}
    return client
      .query({...options, fetchPolicy: "no-cache", query: passwordRequestForgotQuery, variables})
      .then(({data}) => data[passwordRequestForgotKey])
  }

  setForgot(userId: string, confirmation: string, newPassword: string, options: Omit<MutationOptions, "fetchPolicy"> = {}) {
    const variables: MutationPasswordSetFromForgotArgs = {
      userId,
      confirmation,
      newPassword
    }

    return client
      .mutate({...options, fetchPolicy: "no-cache", mutation: passwordSetFromForgotMutation, variables})
      .then(({data}) => data[passwordSetFromForgotKey])
  }

  setLoginChallenge(newPassword: string, challenge: PasswordChallenge, options: Omit<MutationOptions, "fetchPolicy"> = {}) {
    const variables: MutationPasswordSetFromLoginChallengeArgs = {
      challenge,
      newPassword
    }

    return client
      .mutate({...options, fetchPolicy: "no-cache", mutation: passwordSetFromLoginChallengeMutation, variables})
      .then(({data}) => data[passwordSetFromLoginChallengeKey])
  }

  change(user: User, oldPassword: string, newPassword: string, options: Omit<MutationOptions, "fetchPolicy"> = {}) {
    const variables: MutationPasswordSetArgs = {
      userId: user.uuid,
      newPassword,
      oldPassword
    }

    return client
      .mutate({...options, fetchPolicy: "no-cache", mutation: passwordSetMutation, variables})
      .then(({data}) => data[passwordSetKey])
  }

  resendWelcomeEmails(businessId: string, options: Omit<QueryOptions, "fetchPolicy"> = {}) {
    const variables: QueryResendWelcomeEmailsArgs = {businessId}
    return client
      .query({...options, fetchPolicy: "no-cache", query: resendWelcomeEmailsQuery, variables})
      .then(({data}) => data[resendWelcomeEmailsKey])
  }
}

const service = new Password({errorHandlers: [loginErrorHandler]})

export const changePassword = service.change
export const setLoginChallengePassword = service.setLoginChallenge
export const forgotPassword = service.forgot
export const setForgotPassword = service.setForgot
export const resendWelcomeEmails = service.resendWelcomeEmails

export default service
